/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { Box, Grid, List, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { NavBarOcularVisit } from "@components/NavBarOcularVisit";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import ListDocuments from "@components/ListDocuments";
import useInterval from "@hooks/useInterval";
import { STATUS_CATALOG } from "@helpers/constantes";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { setLoanApplication, setLoanStatus } from "@store/slices/registerSlice";
import { LoanStatus } from "@interfaces/catalogs";
import { useNavigate } from "react-router-dom";
import { PersonaStructure } from "@interfaces/index";

const SECONDS_TO_FETCH = 120;

const FirmaDocumentos: React.FC = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const personRepresentante = useSelector((state: RootState) => state.bofu.representante_legal);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const personAvalConyuge = useSelector((state: RootState) => state.bofu.aval_conyuge);

  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();

  const loanInterval = useInterval(async () => {
    try {
      const { firma_otros } = STATUS_CATALOG;
      const loan_application = await getLoanAplication(application.id).unwrap();
      dispatch(setLoanApplication(loan_application));
      if (loan_application?.status_catalog && loan_application?.status_catalog?.code !== firma_otros.code) {
        dispatch(setLoanStatus(loan_application.status_catalog.code as LoanStatus));
        navigate(loan_application.status_catalog.url_actual);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación");
    }
  }, SECONDS_TO_FETCH);

  const getFullName = (person: PersonaStructure) => {
    return [person.name || "", person.last_name || "", person.last_name_2 || ""].join(" ").trim();
  };

  const LoadingSignature = () => {
    return (
      <Box mt={4} display="flex" flexDirection="column" alignItems="center">
        <Skeleton variant="rectangular" sx={{ height: "190px", width: "100%", borderRadius: 2 }} />
      </Box>
    );
  };

  const AvalSignature = () => {
    return (
      <Box>
        <Typography
          mt={4}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.main}
          fontWeight={600}
        >
          ¡La firma de tu empresa se ha registrado con exito!
        </Typography>

        <Typography
          mt={isMobileOrTablet ? 2 : 1}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.main}
          fontWeight={600}
        >
          Ahora enviaremos los documentos para firma electrónica mediante correo electrónico a los firmantes
          adicionales:
        </Typography>

        <Box
          mt={isMobileOrTablet ? 2 : 4}
          sx={{
            backgroundColor: theme?.palette?.phoneInput?.main,
            borderRadius: "10px",
            padding: "20px",
            maxWidth: "98%",
          }}
        >
          <List sx={{ listStyleType: "disc", paddingLeft: 2 }}>
            {personAval && personAval.id !== personRepresentante?.id && (
              <ListDocuments
                title="Aval y Obligado Solidario"
                documents={[getFullName(personAval), personAval.email]}
              />
            )}

            {personAvalConyuge && (
              <ListDocuments
                title="Cónyuge del Aval"
                documents={[getFullName(personAvalConyuge), personAvalConyuge.email]}
              />
            )}
          </List>
        </Box>

        <Typography
          mt={2}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.main}
          fontWeight={600}
        >
          Te recomendamos notificar al los firmantes adicionales para que revisen su correo y completen el proceso de
          firma.
        </Typography>

        <Grid container columnSpacing={1} my={4} justifyContent="center">
          <Grid item xs={6} sm={4} md={3}></Grid>
        </Grid>
      </Box>
    );
  };

  const RenderSignatureView = () => {
    if (personRepresentante && personAval) {
      return <AvalSignature />;
    } else {
      return <LoadingSignature />;
    }
  };

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />

        <Box mx={4}>
          <Typography
            mt={5}
            variant={isMobileOrTablet ? "h5" : "h4"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
            textAlign="center"
          >
            Firma de documentos
          </Typography>

          <NavBarOcularVisit currentStep={4} />

          <RenderSignatureView />
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentos;
